





















































































































































































































import { Component, Prop, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { NavigationGuardNext, Route } from "vue-router";

/**
 * Utils
 */
import * as externalLinks from "@/config/external-links";
import * as imageLinks from "@/config/image-links";

import { colorPlayerCount } from "./lib/helpers";
import { wishesColorFunction } from "@/config/wishes-colors";
import presenceItems from "@/config/presence-items";
import roles, { SubRole } from "@/config/wow/roles";

/**
 * Components
 */
import GroupCard from "@/components/_global/GroupCard.vue";
import WishesTooltipContent from "./components/WishesTooltipContent.vue";
import CompositionsTabList from "./components/CompositionsTabList.vue";
import RosterSelector from "@/components/_global/RosterSelector.vue";
import BuffList from "./components/BuffList.vue";

/**
 * API
 */
import teamsAPI from "@/api/teams";
import rostersAPI from "@/api/rosters";
import compositionsAPI from "@/api/compositions";
import { CompositionGroupResponse, GetOneCompositionGroupResponse } from "@/api/compositions/get-one-composition-group.response";
import { GetOneTeamResponse } from "@/api/teams/get-one-team.response";


/**
 * Types
 */
import { Guid } from "@/domain/guid";
import { GetRostersResponse } from "@/api/rosters/get-rosters.response";
import { GetTeamRaidWishesResponse } from "@/api/teams/get-team-raid-wishes.response";
import DataFetcher from "@/components/_global/DataFetcher.vue";

/**
 * VueX Modules
 */
const teamModule = namespace("team");

/**
 * Compositions' editor page.
 */
@Component({
	components: {
		DataFetcher,
		BuffList,
		GroupCard,
		WishesTooltipContent,
		CompositionsTabList,
		RosterSelector
	},
	beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
		const that = this as CompositionsEditorPage;
		if (!that.hasUnsavedChanges) {
			return next();
		}
		const answer = window.confirm(that.$t("global.alert_leave_page") as string);
		if (answer) {
			next();
		}
		else {
			next(false);
		}
	}
})
export default class CompositionsEditorPage extends Vue {
	/// Injections
	externalLinks = externalLinks;
	imageLinks = imageLinks;
	colorPlayerCount = colorPlayerCount;
	wishesColorFunction = wishesColorFunction;
	presenceItems = presenceItems;

	/// Props
	@Prop()
	compositionGroup!: GetOneCompositionGroupResponse | null;
	@Prop()
	notifyFetchCompositionGroup!: () => Promise<void>;

	/// Attributes
	isLoading = false;
	rosters: GetRostersResponse.Roster[] | null = null;
	wishes: GetTeamRaidWishesResponse | null = null;
	teamMembers: any | null = null;
	hasUnsavedChanges = false;
	activeRosterId: Guid | null = null;
	activeCompositionIndex = 0;
	options: number[] = [];
	clipboardCharacters: CompositionGroupResponse.Character[] | null = null;

	/// VueX Modules
	@teamModule.State("team")
	currentTeam!: GetOneTeamResponse;

	get displayWishes() {
		return this.options.includes(0);
	}

	get displayAlts() {
		return this.options.includes(1);
	}

	get displaySignupsComments() {
		return this.options.includes(2);
	}

	get activeComposition() {
		if (!this.compositionGroup) {
			return null;
		}
		return this.compositionGroup.compositions[this.activeCompositionIndex];
	}

	get activeCompositionCharacters() {
		return this.applySecondaryInformations(this.activeComposition);
	}

	get activeRoster() {
		if (!this.rosters) {
			return null;
		}
		return this.rosters.find(x => x.id == this.activeRosterId) ?? null;
	}

	get activeRosterCharacters() {
		return this.applySecondaryInformations(this.activeRoster);
	}

	get canSave() {
		return !this.isLoading && this.hasUnsavedChanges;
	}

	get canClearCurrentComposition() {
		return !this.isLoading && this.activeComposition && this.activeComposition.characters.length > 0;
	}

	get canAddAllToCurrentComposition() {
		return !this.isLoading;
	}

	get canCopyCurrentComposition() {
		return !this.isLoading && this.activeComposition && this.activeComposition.characters.length > 0;
	}

	get canPasteCharacters() {
		return !this.isLoading && this.clipboardCharacters != null;
	}


	/// Methods
	async created() {
	}

	async fetch() {
		await this.fetchRosters();
		if (this.rosters != null && this.rosters.length > 0) {
			this.activeRosterId = this.rosters[0].id;
		}

		await this.fetchCompositionGroup();

		await this.fetchWishes();
		this.computeAlts();
	}

	async fetchRosters() {
		const rosters = await rostersAPI.fetchRosters(this.currentTeam.id);
		this.rosters = rosters.filter(x => !x.isDisabled);
	}

	async fetchWishes() {
		if (!this.compositionGroup) {
			return;
		}
		if (this.compositionGroup.raidGroupID && this.compositionGroup.raidGroup) {
			const raidIDs = this.compositionGroup.raidGroup.raidIDs;
			const results = await Promise.all(
				raidIDs.map(raidID => teamsAPI.getRaidWishes(this.currentTeam.id, raidID))
			);
			const merged: GetTeamRaidWishesResponse = {};
			for (const result of results) {
				for (const [charId, data] of Object.entries(result)) {
					if (!merged[charId]) {
						merged[charId] = data;
					} else {
						merged[charId] = {
							...merged[charId],
							wishList: { ...merged[charId].wishList, ...data.wishList }
						};
					}
				}
			}
			this.wishes = merged;
			return;
		}
		if (!this.compositionGroup.raidID) {
			return;
		}
		this.wishes = await teamsAPI.getRaidWishes(this.currentTeam.id, this.compositionGroup.raidID);
	}

	computeAlts() {
		if (this.rosters == null) {
			return;
		}

		const allRostersCharacters = this.rosters
			.flatMap(r => r.characters);
		const result: any = {};
		for (const rc of allRostersCharacters) {
			if (rc.userID != null) {
				if (!result[rc.userID]) {
					result[rc.userID] = [];
				}

				if (result[rc.userID].find((c: any) => c.id == rc.id) == null) {
					result[rc.userID].push(rc);
				}
			}
		}
		this.teamMembers = result;
	}

	async fetchCompositionGroup() {
		await this.notifyFetchCompositionGroup();
		this.hasUnsavedChanges = false;
	}

	async handleSaveClick() {
		if (this.rosters == null) {
			return;
		}
		this.isLoading = true;
		try {
			await compositionsAPI.saveAll(this.compositionGroup);
			await this.fetchCompositionGroup();
			this.$snackbar.openInfo(this.$t("snackbar.save_success"));
		} catch (error) {
			this.handleAPIError(error);
		}
		this.isLoading = false;
	}

	private cloneCharacter(c: any) {
		return {...c};
	}

	handleAddAllToCurrentCompositionClick() {
		const activeComposition = this.activeComposition;
		if (activeComposition == null) {
			return;
		}

		const activeRoster = this.activeRoster;
		if (activeRoster == null) {
			return;
		}

		activeComposition.characters = [
			...activeComposition.characters,
			...activeRoster.characters
				.filter(x => !activeComposition.characters.some(ac => x.id === ac.id))
				.map(this.cloneCharacter)
		];
		this.hasUnsavedChanges = true;
	}

	handleClearCurrentCompositionClick() {
		if (this.activeComposition == null) {
			return;
		}

		this.activeComposition.characters = [];
		this.hasUnsavedChanges = true;
	}

	handleCopyCurrentCompositionClick() {
		if (this.activeComposition == null) {
			return;
		}

		this.clipboardCharacters = this.activeComposition.characters;
		this.$snackbar.openInfo(this.$t("snackbar.characters_copied"));
	}

	handlePasteCharactersClick() {
		if (this.activeComposition == null) {
			return;
		}
		if (this.clipboardCharacters == null) {
			return;
		}
		this.activeComposition.characters = this.clipboardCharacters.map(this.cloneCharacter);
		this.hasUnsavedChanges = true;
	}

	handleMoveCharacterInComposition(character: any, previousCompositionID: Guid, nextCompositionID: Guid, role: string) {
		if (this.compositionGroup == null) {
			return;
		}

		this.handleRemoveCharacterFromComposition(character, previousCompositionID);
		this.handleCopyCharacterToComposition(character, nextCompositionID, role);

		this.hasUnsavedChanges = true;
	}

	handleCopyCharacterToComposition(character: any, compositionID: Guid, role: string) {
		if (this.compositionGroup == null) {
			return;
		}
		const compositionToAddCharacterTo = this.compositionGroup.compositions.find(x => x.id === compositionID) ?? null;
		if (compositionToAddCharacterTo == null) {
			return;
		}

		const charactersSet = compositionToAddCharacterTo.characters;
		compositionToAddCharacterTo.characters = [...charactersSet.filter(x => x.id !== character.id), {
			...character,
			role: role
		}];

		this.hasUnsavedChanges = true;
	}

	handleRemoveCharacterFromComposition(character: any, compositionID: Guid) {
		if (this.compositionGroup == null) {
			return;
		}
		const compositionToDeleteCharacterFrom = this.compositionGroup.compositions.find(x => x.id === compositionID) ?? null;
		if (compositionToDeleteCharacterFrom == null) {
			return;
		}

		const charactersSet = compositionToDeleteCharacterFrom.characters;
		compositionToDeleteCharacterFrom.characters = charactersSet.filter(x => x.id !== character.id);

		this.hasUnsavedChanges = true;
	}

	handleNoteChanged() {
		this.hasUnsavedChanges = true;
	}

	handleCharacterContextMenu(character: {
		id: Guid;
	}, rosterID: Guid, ev: Event) {
		if (this.rosters == null) {
			return;
		}
		if (this.compositionGroup == null) {
			return;
		}

		const compositionToModifyCharacterFrom = this.compositionGroup.compositions.find(c => c.id === rosterID) ?? null;
		if (compositionToModifyCharacterFrom == null) {
			return;
		}

		const characterToModify = compositionToModifyCharacterFrom.characters.find(x => x.id == character.id) ?? null;
		if (characterToModify == null) {
			return;
		}

		const roleInfo = roles.find(x => x.name == characterToModify.role) ?? null;
		if (roleInfo == null) {
			return;
		}

		const nonEmptyRoles = roleInfo.subroles.filter(x => x != "") as SubRole[];
		if (nonEmptyRoles.length == 0) {
			return;
		}

		const subRoleIndex = nonEmptyRoles.findIndex(x => x == characterToModify.subRole);

		const numberOfNonEmptyRoles = nonEmptyRoles.length;

		characterToModify.subRole = nonEmptyRoles[(subRoleIndex + 1) % numberOfNonEmptyRoles];

		this.hasUnsavedChanges = true;
	}

	private applySecondaryInformations(o: any) {
		const compositionGroup = this.compositionGroup;
		if (compositionGroup == null) {
			return [];
		}

		const activeComposition = this.activeComposition;
		if (activeComposition == null) {
			return [];
		}

		const signups = compositionGroup.signups;
		if (signups == null) {
			return [];
		}

		const boss = activeComposition.boss;
		return o ? o.characters.map((c: any) => {
			const signup = signups.find(x => x.characters && x.characters.find(xc => xc.id == c.id) != null);
			return {
				...c,
				activeLastLootWishesUpdateTime: this.wishes && this.wishes[c.id] && this.wishes[c.id].lastLootWishesUpdateTime ? new Date(Date.parse(this.wishes[c.id].lastLootWishesUpdateTime)) : null,
				activeWishValue: this.displayWishes && this.wishes && this.wishes[c.id] && boss ? this.wishes[c.id].wishList[boss.id] : null,
				signupState: signup ? signup.signupState : null,
				alts: this.displayAlts && this.teamMembers != null ? this.teamMembers[c.userID]?.filter((x: any) => x.id !== c.id) : null,
				signupComment: this.displaySignupsComments && signup ? signup.comment : null
			};
		}) : [];
	}
}
